import React, { useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from "@mui/material";
import { AddCircleOutline } from "@mui/icons-material";

import { HelpBox } from "@/components/atoms";
import { Variable } from "@/types";


interface VariableCategoryCardProps {
  category: string;
  variables: Variable[];
  onSelect: (variables: Variable[]) => void;
  onDelete: (variable: Variable) => void;
  selected: Set<number>;
  active: Set<number>;
}

const VariableCategoryCard: React.FC<VariableCategoryCardProps> = ({
  category,
  variables,
  onSelect,
  onDelete,
  selected,
  active
}) => {

  const NUM_VARS = 12;
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMore = variables.length > NUM_VARS;
  const visibleVariables = isExpanded ? variables : variables.slice(0, NUM_VARS);

  const handleSelectCategoryVariables = (variables: Variable[]) => {
    // Isolate the variables that aren't selected already
    const updatedVariables = variables.filter(x => !isActive(x));

    // Add them
    onSelect(updatedVariables);
  }

  const isSelected = (variable: Variable) => {
    return selected.has(variable.tag_id);
  };

  const isActive = (variable: Variable) => {
    return active.has(variable.tag_id);
  }

  return (
    <Card elevation={4} sx={{ position: "relative" }}>
      <CardContent sx={{ flexGrow: 1, p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
          <Typography variant="body2">{category}</Typography>
          <Typography variant="caption" color="text.secondary">
            {variables.length} items
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pb: 4 }}>
          {visibleVariables.map((variable) => (
            <Chip
              key={variable.tag_id}
              label={variable.tag_name}
              size="small"
              color={isActive(variable) ? "secondary" : isSelected(variable) ? "info" : "default"}
              onClick={() => !isActive(variable) && onSelect([variable])}
              onDelete={isActive(variable) ? () => onDelete(variable) : undefined}
            />
          ))}

          {hasMore && (
            <Button
              size="small"
              variant="text"
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0, minWidth: 'auto' }}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}

        </Box>
        <HelpBox title="Add all variables to the Query Group">
          <IconButton
            sx={{
              position: "absolute",
              bottom: 8,
              right: 12,
              zIndex: 1,
              p: 0.5,
              borderRadius: "6px",
              color: "divider",
            }}
            onClick={() => handleSelectCategoryVariables(visibleVariables)}
          >
            <AddCircleOutline fontSize="small" />
          </IconButton>
        </HelpBox>
      </CardContent>
    </Card>
  );
};

export default VariableCategoryCard;
